/**
 * Bridges the document to the layout engine: measures every label, builds
 * layout requests/obstacles, and returns final rectangles for drawing and
 * hit-testing. Pure given a Measurer.
 */
import type { ArrowPin, BoxLook, Callout, SharedLabel, Cut, Elevation, Item, MapDoc, Note, Shape, TestPoint, TextRun, Vec, WorkArea } from '../../shared/doc.ts';
import { MATERIALS, markupLabel, markupsIn } from '../../shared/doc.ts';
import { FAIL_LABEL, areasOfLabel, markupLabelId, markupOfLabel } from '../model/markups.ts';
import { depthText, mapPrefix, prefixOf, shownValue, unitOf } from '../model/elev.ts';
import { cutsOf, outlineWithCuts } from '../editor/cuts.ts';
import { composeCallout } from '../model/station.ts';
import { badgeName, badgeRef, ownNameOf, testLabelText, testName } from '../model/testid.ts';
import { materialByKey } from '../../shared/doc.ts';
import { add, clamp, dist, mul, nearestOnPolyline, norm, overlapArea, pointInPoly, polyBounds, polyCentroid, rectCenter, rectsOverlap, rectToPoly, segmentIntersectsRect, segmentsIntersect, sub, type Rect } from '../editor/geom.ts';
import { flattenPath } from '../editor/curve.ts';
import { boxFree, markPoly, placeCallout, rectOverlapsPoly, routeAim, type CalloutSpace, type LeaderAim, type LeaderRoute, type MarkPoly } from '../editor/callout.ts';
import { arrowsOf } from '../model/arrows.ts';
import { clusterHints, layoutLabels, type LabelReq, type Obstacle, type Placement } from '../editor/layout.ts';
import { routeSoilsLeader, type SoilsAround, type SoilsLeader, type SoilsLeaderOpts } from '../editor/soils.ts';
import { areaColorName, areaColors, elevColor, FAIL, FAIL_AREA_FILL, INK, materialColor, PAPER, PASS, type Metrics } from './style.ts';
import type { Measurer } from './text.ts';
import { alignOf, layoutRich, runsOf, widestWord, type Align, type RichText, type TextBase } from './rich.ts';

export interface LabelBox {
  rect: Rect;
  lines: string[];
  weights: number[];
  size: number;
  /** Its words laid out (a pill's; a badge's and a tag's are theirs: `badge.tailText`, `tag.wordsText`). */
  rich?: RichText;
  /** Where its lines sit in it. */
  align?: Align;
  /** How much larger than its kind's own its type is (and its padding with it). */
  k?: number;
  /**
   * How wide it may be made by hand (image px): as narrow as its widest word allows (`min`), and as
   * wide as its words are unwrapped (`nat`) — absent where its width is not for the hand (a box being
   * typed into: it is as wide as what is typed).
   */
  fit?: { min: number; nat: number };
  /** A test's label is a badge: its name in a coloured block, the words after it in a white tail (`lines` still reads the whole label). */
  badge?: Badge;
  /** An elevation shot's label is a tag: its number and its elevation, white on the tags' colour. */
  tag?: ElevTag;
}

/**
 * An elevation shot's tag, as Ricky draws them: one rounded box of the tags' colour, the number and
 * the elevation bold and white in it — "#1  Elev. 2795.65" — the elevation read first at report size.
 */
export interface ElevTag {
  /** "#1". */
  n: string;
  /** "Elev. 2795.65" (and its note). */
  words: string;
  /** The words laid out: wrapped within the tag's width when one was set by hand, the note in its own look. */
  wordsText: RichText;
  /** Where the words' lines begin, from the box's top (each `line` high). */
  wordsTop: number;
  /** Where the words begin, from the box's left. */
  wordsX: number;
  /** The words' start of the value, from the box's left (the in-place editor puts its field there). */
  valueX: number;
  padX: number;
  /** How far below (or above) the grade it was measured against, a line under the words ("9.89 ft below FG"). */
  depth?: string;
  depthSize?: number;
  /** The first line's height: the whole box's without a depth line, a line's pitch with one. */
  line: number;
  /** Where the first line's type and the depth line's sit (their baselines, from the box's top). */
  base: number;
  depthBase?: number;
}

/** An elevation being typed into: what its tag shows of the value meanwhile (the field's text, or its placeholder). */
export interface ElevDraft {
  id: string;
  shown: string;
}

/**
 * A test's badge, as a report at a quarter of a page needs: its name in the map's style — "Test #4"
 * by default, or "#4", "4", "T-4", "Test 4" — bold and white, the size of every other word on the
 * map (2026-10-24, Ricky: the number no larger than the words), in a rounded block of the test's
 * colour, passing green or failing red, one box high and at least as wide; and only when there is
 * more to say (a sand cone test, a retest, a note), those few words dark in a white tail after it,
 * the whole one rounded rectangle the block's height, cornered like every other box on the map.
 * Every badge on every map is the same size, however many tests there are (`badgeBox`).
 */
export interface Badge {
  /** The words before the number ("Test #", "Test "), or ''. */
  pre: string;
  preSize: number;
  /** The number as the style writes it ("4", "#4", "T-4"), or the test's own name (one size with the words before it: the box's `size`). */
  n: string;
  /** The badge's height. */
  d: number;
  /** How wide the coloured block is (square for a short number, wider for more). */
  face: number;
  /** Where the words before the number begin, and the number, from the badge's left. */
  preX: number;
  nX: number;
  /** The words after it, or none: dark on white. */
  tail: string;
  /** The words after it laid out: wrapped within the badge's width when one was set by hand, the note in its own look. */
  tailText: RichText | null;
  tailSize: number;
  /** Where the tail's words begin, from the badge's left. */
  tailX: number;
}

/**
 * What a test's badge says: its name in the map's style — "Test #4" unless another was chosen ("#4",
 * "4", "T-4", "Test 4"; the ID style says "#4", the legend what that stands for) — and after it only
 * what that does not: that it failed, first (2026-10-24, Ricky: a failing test must say so, not only
 * be red: "Failing", or "Failing retest of Test #3"), that it was taken by sand cone (sand cone
 * tests count on their own), the test it retests (called as the badges call it), its note; in the ID
 * style, a test placed under another date or initials than the map's says its whole id there.
 * Failing and passing are its colours too (red; green), as the legend says.
 */
export function badgeText(t: TestPoint, doc: Pick<MapDoc, 'soils'> & { items?: readonly Item[] }): { pre: string; n: string; tail: string } {
  const { pre, n, tail } = badgeParts(t, doc);
  return { pre, n, tail: plain(tail) };
}
/** What a test's badge says after its name, as runs: the words the badge says of itself, plain, then its note in its own look. */
export function badgeTail(t: TestPoint, doc: Pick<MapDoc, 'soils'> & { items?: readonly Item[] }): TextRun[] {
  return badgeParts(t, doc).tail;
}
const plain = (runs: readonly TextRun[]) => runs.map((r) => r.t).join('');
function badgeParts(t: TestPoint, doc: Pick<MapDoc, 'soils'> & { items?: readonly Item[] }): { pre: string; n: string; tail: TextRun[] } {
  const style = doc.soils.testStyle ?? 'test';
  const parts: string[] = [];
  if (style === 'id' && (t.date !== doc.soils.date || t.initials !== doc.soils.initials)) parts.push(testName(t, 'id'));
  // the test it retests, by its own name; a name that says it already ("94R" for the retest of 94) needs no more words
  let retest: string | null = null;
  if (t.retestOf !== null) {
    const own = ownNameOf(doc.items ?? [], t.retestOf, t);
    const of = own ?? String(t.retestOf);
    if (!t.name || !t.name.toLowerCase().includes(of.toLowerCase())) retest = badgeRef(own, t.retestOf, style);
  }
  const failing = t.status === 'fail';
  if (failing) parts.push(retest ? `Failing retest of ${retest}` : 'Failing');
  if (t.method === 'sandcone') parts.push('Sand cone');
  if (retest && !failing) parts.push(`Retest of ${retest}`);
  const said = parts.join(' · ');
  const note = t.rich ?? runsOf(t.note.trim());
  const tail = note.length ? [...(said ? [{ t: `${said} · ` }] : []), ...note] : runsOf(said);
  return { ...badgeName(t, style), tail };
}

/**
 * A badge measured: its block one box high (`boxRatio` × the map's type) and at least as wide, its
 * name centred in it with the box's padding beside it, and its tail of words, if any, padded alike.
 * `k` draws it smaller (the legend's swatch).
 */
export function badgeBox(pre: string, n: string, tail: string | readonly TextRun[], m: Metrics, measure: Measurer, k = 1, width?: number, exact = false): { badge: Badge; w: number; h: number; size: number; fit: { min: number; nat: number } } {
  const size = k * m.font;
  const d = m.boxRatio * size;
  const pad = m.padRatio * size;
  const preW = pre ? measure.width(pre, 700, size) : 0;
  const textW = measure.width(pre + n, 700, size);
  const face = Math.max(d, textW + 2 * pad);
  const preX = (face - textW) / 2;
  const runs = typeof tail === 'string' ? runsOf(tail) : tail;
  let w = face;
  let h = d;
  let tailX = face;
  let tailText: RichText | null = null;
  let fit = { min: face, nat: face };
  if (runs.length) {
    // the words after its name: on one line, or — the badge made narrower by hand — wrapped, the badge as tall as they need
    tailX = face + pad;
    const base: TextBase = { weight: 600, size, color: INK };
    const nat = tailX + layoutRich(runs, base, null, measure).w + pad;
    fit = { min: Math.ceil(tailX + widestWord(runs, base, measure) + pad), nat: Math.ceil(nat) };
    const set = width !== undefined ? Math.max(fit.min, width) : null;
    tailText = layoutRich(runs, base, set !== null ? set - tailX - pad : null, measure);
    // as wide as its words as they wrap there — always just so (`exact`: the width given, for a frame gliding between two such)
    w = set === null ? nat : exact ? set : tailX + tailText.w + pad;
    h = d + (tailText.lines.length - 1) * m.lineHeight * size;
  }
  return { badge: { pre, preSize: size, n, d, face, preX, nX: preX + preW, tail: plain(runs), tailText, tailSize: size, tailX }, w: Math.ceil(w), h: Math.ceil(h), size, fit };
}

/** A test's dot as drawn: its radius and its white ring's width. One size on every map (`markerR`, `markerHalo`), however close tests stand. */
export interface DotSize {
  r: number;
  halo: number;
}

/** A label box's corner radius: every label — a test's badge too — is cornered alike. */
export function labelRadius(_box: LabelBox, m: Metrics): number {
  return m.pillRadius;
}

/**
 * A callout while it is being typed into: its lines as the in-place editor shows them, piece by
 * piece (fields and the words between them, placeholders included), so the box on the plan is
 * exactly the editor's box.
 */
export interface CalloutDraft {
  id: string;
  lines: string[][];
}
/** A callout's box plus the leaders routed for it: one per arrow, in the order of `arrowsOf`. */
export interface CalloutBox extends LabelBox {
  /** Being typed into: the in-place editor shows the words, the plan only the box. */
  editing?: boolean;
  leaders: LeaderRoute[];
}

export interface Layout {
  tests: Map<string, LabelBox>;
  /** The elevation shots' tags, by the shot's id. */
  elevs: Map<string, LabelBox>;
  /**
   * The soils labels' leaders, routed round everything else: a test's one to its dot (by the test's
   * id), and the shared labels' arrows, one to each of their areas ('__area', '__fail').
   */
  soilsLeaders: Map<string, SoilsLeader[]>;
  callouts: Map<string, CalloutBox>;
  notes: Map<string, LabelBox>;
  /** Every shared label: each markup's ('__area', '__area2' …) and the failing areas' ('__fail'). */
  shared: Map<string, LabelBox>;
  /** Markup 1's label (`shared.get('__area')`). */
  areaLabel: LabelBox | null;
  /** The failing areas' shared label. */
  failLabel: LabelBox | null;
  /** How far out the badges were laid out — 0 near, 1 with the farther rings, 2 farther still (kept from layout to layout while a label is carried). */
  badges: { far: number };
  /** The tests' dots as drawn: one size on every map. */
  dot: DotSize;
  /** The elevation shots' rings as drawn, the size of the tests' dots. */
  elevDot: DotSize;
  /** The legend as it will be drawn (a soils map's labels keep off it), or null when the map has none. */
  legend: LegendLayout | null;
  /** The tests' and shots' labels placed automatically (not put by hand): only these are kept where they were by the layout after. */
  autoLabels: Set<string>;
}

/** What else the layout keeps labels off: the plan's own lettering (boxes in image px, from its reading), when it is known. */
export interface PlanAround {
  textBoxes?: readonly Rect[];
}

export function shapeBounds(shape: WorkArea['shape']): Rect {
  switch (shape.k) {
    case 'rect':
      return { x: Math.min(shape.x, shape.x + shape.w), y: Math.min(shape.y, shape.y + shape.h), w: Math.abs(shape.w), h: Math.abs(shape.h) };
    case 'poly':
      return polyBounds(shape.pts);
    case 'path':
      return polyBounds(flattenPath(shape.pts, shape.h));
    case 'stroke': {
      const b = polyBounds(shape.pts);
      const r = shape.width / 2;
      return { x: b.x - r, y: b.y - r, w: b.w + 2 * r, h: b.h + 2 * r };
    }
  }
}

/** Nearest point on a shape's outline to p (where an area's arrow ends). */
export function nearestOnShape(s: Shape, p: Vec): Vec {
  if (s.k === 'rect') return nearestOnPolyline(p, rectToPoly(shapeBounds(s)), true).p;
  if (s.k === 'poly') return nearestOnPolyline(p, s.pts, true).p;
  if (s.k === 'path') return nearestOnPolyline(p, flattenPath(s.pts, s.h), true).p;
  const c = nearestOnPolyline(p, s.pts, false).p;
  const d = norm(sub(p, c));
  return add(c, mul(d, s.width / 2));
}

export function shapeCenter(shape: WorkArea['shape']): { x: number; y: number } {
  if (shape.k === 'poly') return polyCentroid(shape.pts);
  if (shape.k === 'path') return polyCentroid(flattenPath(shape.pts, shape.h));
  return rectCenter(shapeBounds(shape));
}

export function failLabelText(doc: MapDoc, count: number): string {
  const custom = doc.soils.failLabel?.text;
  if (custom !== null && custom !== undefined && custom.trim() !== '') return custom.trim();
  return count > 1 ? 'Failing areas' : 'Failing area';
}

/** A markup's label's words: as typed, else "Work area(s) observed". */
export function areaLabelText(doc: MapDoc, count: number, markup = 1): string {
  const custom = markupLabel(doc.soils, markup).text;
  if (custom !== null && custom.trim() !== '') return custom.trim();
  return count > 1 ? 'Work areas observed' : 'Work area observed';
}

/**
 * A callout's colour: its material's on a concrete map. On a soils map, the colour of what it
 * points at — a failing area's red, a work area's amber — else ink.
 */
export function calloutColor(doc: MapDoc, c: Callout): string {
  if (doc.kind === 'concrete' || c.material) return materialColor(c.material);
  for (const a of arrowsOf(c)) {
    const it = a.targetId ? doc.items.find((i) => i.id === a.targetId) : undefined;
    if (it?.t === 'area') return areaStroke(it);
  }
  return INK;
}

/** A work area's line colour: its own colour's deep shade (a failing area's red). */
export function areaStroke(a: Pick<WorkArea, 'status' | 'color'>): string {
  return a.status === 'fail' ? FAIL : areaColors(a.color).stroke;
}
/**
 * A shared label's colour: the failing areas' red; a markup's label takes its markup's colour (the
 * first of its areas': each arrow is in its own area's colour).
 */
export function sharedLabelColor(doc: MapDoc, id: string): string {
  if (id === FAIL_LABEL) return FAIL;
  const first = areasOfLabel(doc.items, id)[0];
  return first ? areaStroke(first) : areaColors(null).stroke;
}

/** The material word a callout shows: as typed, else its material's name. */
export function calloutMaterialName(c: Pick<Callout, 'material' | 'materialName'>): string {
  return c.materialName ?? (c.material ? materialByKey(c.material).name : '');
}
export function calloutLines(c: Callout): string[] {
  return composeCallout({ template: c.template, material: calloutMaterialName(c), alignment: c.alignment, from: c.from, to: c.to, custom: c.custom, ...(c.ranges ? { ranges: c.ranges } : {}) });
}
/** The marks of a document as polygons, and what each of a callout's arrows aims at. */
export interface CalloutContext {
  space: CalloutSpace;
  /** One aim per arrow (in the order of `arrowsOf`, each with its place there): where the person put the tip, else the middle of the mark; an arrow taken off says so (`off`). */
  aimsOf(c: Callout): LeaderAim[];
}
const cutShapes = new WeakMap<number[], Shape>();
/** A mark's shape as it reads with its cuts (the same shape object for the same outline, so its reading is kept). */
function asCut(shape: Shape, cuts: readonly Cut[]): Shape {
  if (!cuts.length) return shape;
  const pts = outlineWithCuts(shape, cuts);
  let s = cutShapes.get(pts);
  if (!s) cutShapes.set(pts, (s = { k: 'poly', pts }));
  return s;
}
export function calloutContext(doc: MapDoc, m: Metrics): CalloutContext {
  const marks: MarkPoly[] = [];
  // a mark as it reads with what is cut out of it: an arrow never aims into a notch
  for (const it of doc.items) if (it.t === 'hl' || it.t === 'area') marks.push(markPoly(it.id, asCut(it.shape, cutsOf(doc.items, it.id))));
  const space: CalloutSpace = { marks, bounds: { x: 0, y: 0, w: doc.w, h: doc.h }, u: m.u, minGap: m.calloutGap, arrowLen: m.arrowLen, stub: m.leaderStub, tipGap: m.tipGap };
  return {
    space,
    aimsOf: (c) =>
      arrowsOf(c).map((a, arrow) => {
        // an arrow belongs to the mark it was made for; a free one whose point lies on a mark points at that mark
        const mark = (a.targetId ? marks.find((mk) => mk.id === a.targetId) : undefined) ?? marks.find((mk) => pointInPoly(a.target, mk.pts)) ?? null;
        const pinned = a.pinned === true && mark !== null;
        // aimed at the middle of its mark — or where that middle is nearest on the plan, for a mark run off it
        const mid = mark ? { x: clamp(mark.mid.x, 0, doc.w), y: clamp(mark.mid.y, 0, doc.h) } : null;
        return { mark, point: pinned || !mid ? a.target : mid, arrow, ...(pinned ? { pinned, ...(a.face ? { face: a.face } : {}) } : {}), ...(a.off ? { off: true } : {}) };
      }),
  };
}

/** A callout's words as runs: its own words in their own look, or the lines its fields make. */
export function calloutRuns(c: Callout): TextRun[] {
  if (c.template === 'custom' && c.rich) return c.rich;
  return runsOf(calloutLines(c).join('\n'));
}
export function calloutSize(c: Callout, m: Metrics, measure: Measurer, draft: CalloutDraft | null = null, exact = false): Pill & { editing?: boolean } {
  const k = c.look?.size ?? 1;
  if (draft && draft.id === c.id) {
    // the editor lays its pieces side by side, each as wide as it measures: so does the box (as wide as what is typed)
    const size = m.fontCallout * k;
    const lines = draft.lines.map((pieces) => pieces.join(''));
    const tw = Math.max(0, ...draft.lines.map((pieces) => pieces.reduce((sum, t) => sum + measure.width(t, 600, size), 0)));
    const rich = layoutRich(runsOf(lines.join('\n')), { weight: 600, size, color: INK }, null, measure);
    return { lines, weights: lines.map(() => 600), w: Math.ceil(tw + 2 * m.pillPadX * k), h: Math.ceil(lines.length * size * m.lineHeight + 2 * m.pillPadY * k), rich, k, size, editing: true };
  }
  return pillBox(calloutRuns(c), 600, m.fontCallout, c.look, m, measure, exact);
}

/**
 * A label's box measured with its words wrapped within a width (none: on one line), as the layout
 * would make it: as wide as they then are, its padding even either side — or (`exact`) as wide as that
 * width, for a frame gliding between two such. Null for what is no label (`id`: a markup's or the
 * failing areas' label, a test, a shot, a callout, a note).
 */
export function labelMeasure(doc: MapDoc, id: string, m: Metrics, measure: Measurer): ((w?: number, exact?: boolean) => { w: number; h: number }) | null {
  const at = <T extends { look?: BoxLook }>(o: T, w?: number): T => {
    const { w: _w, ...look } = o.look ?? {};
    return { ...o, look: w === undefined ? look : { ...look, w } };
  };
  if (id.startsWith('__')) {
    const n = id === FAIL_LABEL ? 0 : (markupOfLabel(id) ?? 1);
    const label = n ? markupLabel(doc.soils, n) : doc.soils.failLabel;
    const list = areasOfLabel(doc.items, id);
    const text = n ? areaLabelText(doc, list.length, n) : failLabelText(doc, list.length);
    const runs = label?.rich && label.text?.trim() ? label.rich : runsOf(text);
    return (w, exact) => {
      const p = pillBox(runs, 600, m.font, at(label?.look ? { look: label.look } : {}, w).look, m, measure, exact);
      return { w: p.w, h: p.h };
    };
  }
  const it = doc.items.find((i) => i.id === id);
  if (it?.t === 'test') return (w, exact) => {
    const b = badgeOf(at(it, w), doc, m, measure, exact);
    return { w: b.w, h: b.h };
  };
  if (it?.t === 'elev') return (w, exact) => {
    const t = elevTagOf(at(it, w), doc, m, measure, null, exact);
    return { w: t.w, h: t.h };
  };
  if (it?.t === 'callout') return (w, exact) => {
    const p = calloutSize(at(it, w), m, measure, null, exact);
    return { w: p.w, h: p.h };
  };
  if (it?.t === 'note') return (w, exact) => {
    const p = pillBox(it.rich ?? runsOf(it.text), 600, m.font, at(it, w).look, m, measure, exact);
    return { w: p.w, h: p.h };
  };
  return null;
}
/** A box of words as laid out: its size, its lines, and how wide the hand may make it. */
interface Pill {
  lines: string[];
  weights: number[];
  w: number;
  h: number;
  rich: RichText;
  k: number;
  size: number;
  fit?: { min: number; nat: number };
}
/**
 * A box of words (a label's, a callout's, a note's) measured: as wide as its words — or as wide as
 * set by hand, never narrower than its widest word, its words wrapping within it — and as tall as
 * its lines; its type and its padding at its size (× its kind's `size`).
 */
function pillBox(runs: readonly TextRun[], weight: number, size0: number, look: BoxLook | undefined, m: Metrics, measure: Measurer, exact = false): Pill {
  const k = look?.size ?? 1;
  const size = size0 * k;
  const padX = m.pillPadX * k;
  const base: TextBase = { weight, size, color: INK };
  const nat = Math.ceil(layoutRich(runs, base, null, measure).w + 2 * padX);
  const min = Math.ceil(widestWord(runs, base, measure) + 2 * padX);
  const set = look?.w !== undefined ? Math.max(min, look.w) : null;
  const rich = layoutRich(runs, base, set !== null ? set - 2 * padX : null, measure);
  // as wide as its words as they wrap at the width set — always just so, its padding even either side
  // (`exact`: the width given, for a frame gliding between two such)
  const w = set === null ? nat : exact ? set : Math.ceil(rich.w + 2 * padX);
  const lines = rich.lines.map((l) => l.pieces.map((p) => p.t).join(''));
  return { lines, weights: lines.map(() => weight), w: Math.ceil(w), h: Math.ceil(lines.length * size * m.lineHeight + 2 * m.pillPadY * k), rich, k, size, fit: { min, nat } };
}
/** A pill as a label box (its place to come). */
function pillLabel(p: Pill, look: BoxLook | undefined): LabelBox {
  return { rect: { x: 0, y: 0, w: p.w, h: p.h }, lines: p.lines, weights: p.weights, size: p.size, rich: p.rich, align: alignOf(look), k: p.k, ...(p.fit ? { fit: p.fit } : {}) };
}

/**
 * A callout box — or a soils label (a test's, by its id, or '__area' / '__fail') — being carried
 * by the finger. Over a place where it may not rest it is adrift: nothing goes round it (it is only
 * passing), and its own leaders are routed roughly, straight to what they point at (they are drawn
 * faded). While a soils label is carried the others stay exactly where they were. (`leaders` is no
 * longer read: an adrift label always keeps its leaders.)
 */
export interface Carried {
  id: string;
  adrift: boolean;
  leaders?: boolean;
}

/**
 * An elevation shot's tag measured: one box high (`boxRatio` × its type, the map's smaller type —
 * three quarters of a badge's number), padded as every box is, "#1" and its words a word's space
 * apart; measured against a grade, the depth is a second line in the same type under the words.
 */
function elevTagOf(e: Elevation, doc: MapDoc, m: Metrics, measure: Measurer, draft: ElevDraft | null, exact = false): { tag: ElevTag; w: number; h: number; text: string; size: number; k: number; fit?: { min: number; nat: number } } {
  const k = e.look?.size ?? 1;
  const font = m.font * k;
  const n = `#${e.n}`;
  const prefix = prefixOf(e, doc.elev);
  // being typed: as wide as what is typed (the field stands on the value, on the first line)
  const typing = draft?.id === e.id;
  const value = typing ? draft.shown : shownValue(e.value) || '—';
  const said = [prefix, value].filter(Boolean).join(' ');
  // its note after it, in its own look
  const note = e.rich ?? runsOf(e.note ?? '');
  const runs: TextRun[] = note.length ? [{ t: `${said} ` }, ...note] : runsOf(said);
  const words = plain(runs);
  const padX = m.padRatio * font;
  const gap = 0.35 * font;
  const wordsX = padX + measure.width(n, 700, font) + gap;
  const valueX = wordsX + (prefix ? measure.width(`${prefix} `, 700, font) : 0);
  const base: TextBase = { weight: 700, size: font, color: PAPER };
  // measured against a grade the inspector typed: how far below it, on a line of its own under the words
  const depth = depthText(e.value, e.ref, doc.elev);
  const depthW = depth ? measure.width(depth, 600, font) : 0;
  const nat = Math.ceil(wordsX + Math.max(layoutRich(runs, base, null, measure).w, depthW) + padX);
  const min = Math.ceil(wordsX + Math.max(widestWord(runs, base, measure), depthW) + padX);
  const set = !typing && e.look?.w !== undefined ? Math.max(min, e.look.w) : null;
  // the words wrap after its number, in a column of their own, when the tag was made narrower by hand
  const wordsText = layoutRich(runs, base, set !== null ? set - wordsX - padX : null, measure);
  const count = wordsText.lines.length + (depth ? 1 : 0);
  let line = m.boxRatio * font;
  let h = line;
  let top = 0;
  let base0 = line / 2 + 0.36 * font;
  let depthBase: number | undefined;
  if (count > 1) {
    // lines a line apart, with the box's own margin above and below them
    const padY = ((m.boxRatio - m.lineHeight) / 2) * font;
    line = m.lineHeight * font;
    top = padY;
    base0 = padY + line / 2 + 0.36 * font;
    if (depth) depthBase = base0 + wordsText.lines.length * line;
    h = 2 * padY + count * line;
  }
  // as wide as its words as they wrap there — always just so (`exact`: the width given, for a frame gliding between two such)
  const w = set === null ? nat : exact ? set : wordsX + Math.max(wordsText.w, depthW) + padX;
  return {
    tag: { n, words, wordsText, wordsTop: top, wordsX, valueX, padX, line, base: base0, ...(depth && depthBase !== undefined ? { depth, depthSize: font, depthBase } : {}) },
    w: Math.ceil(w),
    h: Math.ceil(h),
    text: `${n} ${words}${depth ? ` (${depth})` : ''}`,
    size: font,
    k,
    ...(typing ? {} : { fit: { min, nat } }),
  };
}

/** A test's badge measured, as its map's style names it, at its size and width set by hand. */
function badgeOf(t: TestPoint, doc: MapDoc, m: Metrics, measure: Measurer, exact = false): ReturnType<typeof badgeBox> {
  const { pre, n, tail } = badgeParts(t, doc);
  return badgeBox(pre, n, tail, m, measure, t.look?.size ?? 1, t.look?.w, exact);
}

/** How a layout is to be made. */
export interface LayoutOptions {
  /**
   * Hold every test's and shot's label exactly where it was (2026-10-24, Ricky: moving a dot must not
   * move its label): while a pinpoint is dragged, only the leaders follow it. Let go, the next layout
   * keeps each label where it is unless its place has become a poor one.
   */
  holdAll?: boolean;
  /**
   * The id of a box drawn for a moment exactly as wide as its look says (its words wrapped within that),
   * rather than as wide as they then are: the frame of a box gliding from the width its words had to
   * the width they have now (editor.ts, its grips).
   */
  exact?: string;
}

export function computeLayout(doc: MapDoc, m: Metrics, measure: Measurer, prev: ReadonlyMap<string, Rect>, draft: CalloutDraft | null = null, carried: Carried | null = null, elevDraft: ElevDraft | null = null, plan: PlanAround = {}, options: LayoutOptions = {}): Layout {
  const bounds: Rect = { x: 0, y: 0, w: doc.w, h: doc.h };
  const obstacles: Obstacle[] = [];
  const boxes = new Map<string, LabelBox>();
  const soils = doc.kind === 'soils';

  const all = doc.items.filter((it): it is WorkArea => it.t === 'area');
  const tests = doc.items.filter((it): it is TestPoint => it.t === 'test');
  const elevs = doc.items.filter((it): it is Elevation => it.t === 'elev');
  const callouts = doc.items.filter((it): it is Callout => it.t === 'callout');
  const notes = doc.items.filter((it): it is Note => it.t === 'note');

  const cc = calloutContext(doc, m);
  // Keep labels off the work areas: firmly off an area's edge (its colour band and a little either
  // side, where a label would hide where the area ends), and mildly off its body, of the area itself
  // and not the box round it — a test's label sits outside its area where there is room near by,
  // but on the area's tint rather than far off when the area is large.
  for (const a of all) {
    const mk = cc.space.marks.find((k) => k.id === a.id);
    if (mk) for (const r of edgeBand(mk.pts, 6 * m.u, 24 * m.u)) obstacles.push({ rect: r, weight: 4 });
    for (const r of areaCells(a.shape, mk, m)) obstacles.push({ rect: r, weight: 2, group: a.id });
  }
  for (const it of doc.items) if (it.t === 'hl') obstacles.push({ rect: shapeBounds(it.shape), weight: 3 });
  // the plan's own lettering, when it has been read: a label would rather not hide it
  for (const r of plan.textBoxes ?? []) obstacles.push({ rect: r, weight: 2, fresh: true });
  // the legend, in its corner: on a soils map no label or leader goes under it
  const legend = doc.legend ? legendLayout(doc, m, measure) : null;
  const fixed: Rect[] = [];
  if (legend && soils) fixed.push({ x: legend.rect.x - m.u, y: legend.rect.y - m.u, w: legend.rect.w + 2 * m.u, h: legend.rect.h + 2 * m.u });

  // Notes are always explicitly positioned; they are obstacles for everything else.
  const noteBoxes = new Map<string, LabelBox>();
  for (const n of notes) {
    const box = pillLabel(pillBox(n.rich ?? runsOf(n.text), 600, m.font, n.look, m, measure, options.exact === n.id), n.look);
    const rect = { x: n.p.x, y: n.p.y, w: box.rect.w, h: box.rect.h };
    noteBoxes.set(n.id, { ...box, rect });
    obstacles.push({ rect, weight: 5 });
    if (n.target) obstacles.push({ rect: { x: n.target.x - m.markerR, y: n.target.y - m.markerR, w: 2 * m.markerR, h: 2 * m.markerR }, weight: 5 });
  }

  // The shared labels — each markup's ("Work area(s) observed", or its own words) with an arrow to
  // every area of it, and "Failing area(s)" to every failing one — are callouts of the map's own: laid
  // out and routed exactly as callouts are.
  const shared: Shared[] = [];
  // (one deleted from the map is not drawn and keeps no room: its areas, and its line in the legend, stay)
  const lists: { id: string; list: WorkArea[]; text: string; label: SharedLabel | undefined }[] = markupsIn(doc.items).map((n) => {
    const id = markupLabelId(n);
    const list = areasOfLabel(doc.items, id);
    return { id, list, text: areaLabelText(doc, list.length, n), label: markupLabel(doc.soils, n) };
  });
  const failing = areasOfLabel(doc.items, FAIL_LABEL);
  lists.push({ id: FAIL_LABEL, list: failing, text: failLabelText(doc, failing.length), label: doc.soils.failLabel });
  for (const { id, list, text, label } of lists) {
    if (!list.length || label?.hidden) continue;
    // its words in their own look (when they are its own: an automatic label is plain)
    const box = pillLabel(pillBox(label?.rich && label.text?.trim() ? label.rich : runsOf(text), 600, m.font, label?.look, m, measure, options.exact === id), label?.look);
    boxes.set(id, box);
    shared.push({ id, aims: sharedAims(cc, list, label?.pins ?? {}), pos: label?.pos ?? null, w: box.rect.w, h: box.rect.h });
  }

  // Test points and elevation shots: badges and tags (`badgeBox`), radiating outward from clusters —
  // laid out once the callouts' boxes are known (below)
  const spots = [...tests.map((t) => ({ id: t.id, p: t.p })), ...elevs.map((e) => ({ id: e.id, p: e.p }))];
  const hints = clusterHints(spots, Math.max(doc.w, doc.h) * 0.12);
  // the dots: one size on every map, however many tests or however close (2026-10-23, Ricky: nothing
  // may shrink as tests are added) — or none at all, when the map says so: every leader then simply
  // ends on its spot, its white casing round the end; what each keeps clear round it
  const dot: DotSize = doc.noDots ? { r: 0, halo: 0 } : { r: m.markerR, halo: m.markerHalo };
  const elevDot = dot;
  const dotReach = dot.r > 0 ? dot.r + dot.halo : m.leader / 2 + m.leaderHalo;
  const elevReach = dotReach;
  const keepOut = dotReach + m.gap;
  const elevKeepOut = keepOut;

  // Callouts (and the shared labels): above or below their marks, leaders from the box's top/bottom
  // centre, never across a mark. Boxes first, leaders after: every leader is routed knowing where
  // every box is, so none runs under another callout or a note, and each keeps clear of the
  // leaders drawn before it.
  const calloutBoxes = new Map<string, CalloutBox>();
  const noteRects: Rect[] = [...[...noteBoxes.values()].map((b) => b.rect), ...fixed];
  const sizes = new Map<string, ReturnType<typeof calloutSize>>();
  const rects = new Map<string, Rect>();
  /** What each callout-like box points at: a callout's arrows, a shared label's areas. */
  const aimsOf = new Map<string, LeaderAim[]>();
  const onPlan = (at: Vec, w: number, h: number): Rect => ({ x: clamp(at.x, bounds.x + m.u, bounds.x + bounds.w - w - m.u), y: clamp(at.y, bounds.y + m.u, bounds.y + bounds.h - h - m.u), w, h });
  for (const c of callouts) {
    const size = calloutSize(c, m, measure, draft, options.exact === c.id);
    sizes.set(c.id, size);
    aimsOf.set(c.id, cc.aimsOf(c));
    // a box put by hand stays on the plan (one carried along with its mark to the edge is held there)
    if (c.box) rects.set(c.id, onPlan(c.box, size.w, size.h));
  }
  for (const sl of shared) {
    aimsOf.set(sl.id, sl.aims);
    if (sl.pos) rects.set(sl.id, onPlan(sl.pos, sl.w, sl.h));
  }
  const boxed = [...callouts.map((c) => c.id), ...shared.map((sl) => sl.id)];
  /** The leaders of one box: each arrow aims at the middle of its mark, or exactly where the person put it. */
  const adrift = carried?.adrift && boxed.includes(carried.id) ? carried.id : null;
  /** The arrows of a box that are drawn: those not taken off. */
  const live = (id: string) => (aimsOf.get(id) ?? []).filter((a) => !a.off);
  const lead = (id: string, rect: Rect, drawn: readonly (readonly Vec[])[]): LeaderRoute[] => {
    const routes: LeaderRoute[] = [];
    const aims = live(id);
    if (id === adrift) {
      // passing over somewhere it may not rest: a rough line to each of its marks (drawn faded)
      for (const aim of aims) routes.push({ ...routeAim(rect, aim, cc.space, { boxes: [], leaders: [] }, false), ...(aim.arrow !== undefined ? { arrow: aim.arrow } : {}) });
      return routes;
    }
    // (on a soils map the test labels, the shared area labels and every test's dot are in the way too)
    const others = [...noteRects, ...[...rects].filter(([k]) => k !== id && k !== adrift).map(([, r]) => r)];
    for (const aim of aims) routes.push({ ...routeAim(rect, aim, cc.space, { boxes: others, leaders: [...drawn, ...routes.map((r) => r.pts)] }), ...(aim.arrow !== undefined ? { arrow: aim.arrow } : {}) });
    return routes;
  };
  // the callouts' leaders as they will run: the test labels are placed so that theirs need not cross them
  const early: Vec[][] = [];
  const pending = [...callouts.filter((c) => !c.box).map((c) => ({ id: c.id, size: sizes.get(c.id)! })), ...shared.filter((sl) => !sl.pos).map((sl) => ({ id: sl.id, size: { w: sl.w, h: sl.h } }))];
  // (words being typed: a callout's in place, or a shot's elevation)
  const wordsTyped = !!draft || !!elevDraft;
  if (pending.length) {
    // where the boxes placed by hand lead to, so that an automatic box does not sit on those leaders
    const drawn: Vec[][] = [];
    for (const id of boxed) if (rects.has(id)) for (const r of lead(id, rects.get(id)!, drawn)) drawn.push(r.pts);
    // the tests' dots and the shots' rings, as a box placed afresh keeps off them
    const dotRects = spots.map((sp) => ({ x: sp.p.x - keepOut, y: sp.p.y - keepOut, w: 2 * keepOut, h: 2 * keepOut }));
    for (const { id, size } of pending) {
      // steady where it was — unless it points at more or fewer marks than then: that is a new question
      // (with every arrow taken off, it is still placed by its marks: the routes are not drawn)
      const drawnAims = live(id);
      const aims = drawnAims.length ? drawnAims : aimsOf.get(id)!;
      const was = prev.get(`${id}#${drawnAims.length}`) ?? null;
      // A box placed afresh keeps off every dot. Once it has its place, tests play no part: a test
      // placed never moves a box or its arrows (the tests' labels keep off them instead) — but nor
      // does a box move onto a dot it stood clear of: then it stays where it was, if it still may.
      const taken = [...noteRects, ...rects.values()];
      // while words are typed (a callout's, a shot's), a box that has its place keeps it — unless what is
      // typed has grown onto it: typing stays quick on a crowded map, and nothing moves but what is typed
      const held = wordsTyped && was ? { x: clamp(was.x, bounds.x + m.u, bounds.x + bounds.w - size.w - m.u), y: clamp(was.y, bounds.y + m.u, bounds.y + bounds.h - size.h - m.u), w: size.w, h: size.h } : null;
      // (its arrows are routed with every other's, below: none is placed afresh meanwhile to keep off them)
      if (held && !taken.some((r) => rectsOverlap(r, held)) && boxFree(held, aims.map((a) => a.mark), cc.space, taken)) {
        rects.set(id, held);
        continue;
      }
      let { rect, routes } = placeCallout(size, aims, cc.space, { avoid: was ? taken : [...taken, ...dotRects], leaders: drawn, prev: was });
      if (was && dotRects.some((d) => rectsOverlap(d, rect)) && !dotRects.some((d) => rectsOverlap(d, was))) {
        const at = { x: was.x, y: was.y, w: size.w, h: size.h };
        if (!dotRects.some((d) => rectsOverlap(d, at)) && boxFree(at, aims.map((a) => a.mark), cc.space, taken)) {
          rect = at;
          routes = [];
          for (const aim of aims) routes.push(routeAim(at, aim, cc.space, { boxes: taken, leaders: [...drawn, ...routes.map((r) => r.pts)] }));
        }
      }
      rects.set(id, rect);
      if (drawnAims.length) for (const r of routes) drawn.push(r.pts);
    }
  }
  // Every callout's and shared label's arrows now, as they will be drawn: routed round the other boxes,
  // but never round a test — its dot or its label: a test placed never moves an arrow. The ones with
  // nothing in their way are drawn first and stand; the ones that must find a way round something then
  // find it round those too — so a detour never pushes a plain leader about.
  const drawn: Vec[][] = [];
  const routed = new Map<string, LeaderRoute[]>();
  const direct = new Set(boxed.filter((id) => id !== adrift && lead(id, rects.get(id)!, []).every((r) => !r.detour)));
  for (const id of [...boxed.filter((id) => direct.has(id)), ...boxed.filter((id) => !direct.has(id))]) {
    const leaders = lead(id, rects.get(id)!, drawn);
    if (id !== adrift) for (const r of leaders) drawn.push(r.pts);
    routed.set(id, leaders);
  }
  early.push(...drawn);
  // their heads, with room round them: no test's or shot's label comes up against an arrowhead
  const heads: Rect[] = [];
  for (const [id, routes] of routed) {
    if (id === adrift) continue;
    for (const r of routes) {
      const back = { x: r.tip.x - r.dir.x * m.arrowLen, y: r.tip.y - r.dir.y * m.arrowLen };
      const half = m.arrowWidth / 2 + m.leaderHalo + 3 * m.u;
      heads.push({ x: Math.min(r.tip.x, back.x) - half, y: Math.min(r.tip.y, back.y) - half, w: Math.abs(r.tip.x - back.x) + 2 * half, h: Math.abs(r.tip.y - back.y) + 2 * half });
    }
  }
  // The test labels and elevation tags next, clear of every callout box. While a soils label (a
  // test's, a shot's or a shared one) is carried the others stay exactly where they were (it is only
  // passing), and one carried where it may not rest is not kept clear of at all; a label once pinned
  // off the plan comes back onto it.
  for (const r of rects.values()) obstacles.push({ rect: r, weight: 5 });
  const spotIds = new Set(spots.map((s) => s.id));
  const carriedLabel = carried && spotIds.has(carried.id) ? carried : null;
  // a shot being typed into holds the others still too (its tag alone grows): typing stays quick on a crowded map
  const typing = elevDraft && spotIds.has(elevDraft.id) ? { id: elevDraft.id } : null;
  const holding = carriedLabel ?? (carried && shared.some((sl) => sl.id === carried.id) ? carried : null) ?? typing ?? (options.holdAll ? { id: '' } : null);
  const dotBoxes: Rect[] = [...tests.map((t) => ({ x: t.p.x - dotReach, y: t.p.y - dotReach, w: 2 * dotReach, h: 2 * dotReach })), ...elevs.map((e) => ({ x: e.p.x - elevReach, y: e.p.y - elevReach, w: 2 * elevReach, h: 2 * elevReach }))];
  const ownDot = new Map<string, Rect>();
  [...tests, ...elevs].forEach((s, i) => ownDot.set(s.id, dotBoxes[i]!));
  // every badge and tag measured once: they are the same size however they are laid out
  const measured = new Map<string, LabelBox>();
  for (const t of tests) {
    const { badge, w, h, size, fit } = badgeOf(t, doc, m, measure, options.exact === t.id);
    // (a badge with no words after its name has nothing to wrap: its width is not for the hand)
    measured.set(t.id, { rect: { x: 0, y: 0, w, h }, lines: [testLabelText(t, doc.soils.testStyle ?? 'test', doc.items)], weights: [700], size, badge, align: alignOf(t.look), k: t.look?.size ?? 1, ...(badge.tailText ? { fit } : {}) });
  }
  for (const e of elevs) {
    const { tag, w, h, text, size, k, fit } = elevTagOf(e, doc, m, measure, elevDraft, options.exact === e.id);
    measured.set(e.id, { rect: { x: 0, y: 0, w, h }, lines: [text], weights: [700], size, tag, align: alignOf(e.look), k, ...(fit ? { fit } : {}) });
  }
  /** The tests' badges and the shots' tags laid out (with the farther rings, or not) — and how many of those placed automatically lie on something (another label, a box, a dot). */
  const placeSpots = (far: number): { placed: Placement[]; boxes: Map<string, LabelBox>; bad: number } => {
    const bx = new Map<string, LabelBox>();
    const reqs: LabelReq[] = [];
    const request = (id: string, p: Vec, w: number, h: number, pinned: Vec | null, ko: number, priority: number, sides?: 'lr') => {
      const hint = hints.get(id) ?? { hint: null, spread: 0 };
      const q: LabelReq = { id, anchor: p, w, h, pinned, keepOut: ko, hint: hint.hint, spread: hint.spread, priority, ...(sides ? { sides } : {}) };
      if (holding && q.id !== holding.id && !q.pinned) {
        const was = prev.get(q.id);
        if (was) q.pinned = { x: was.x, y: was.y };
      }
      if (carriedLabel?.adrift && q.id === carriedLabel.id) q.ghost = true;
      else if (q.pinned) q.pinned = { x: clamp(q.pinned.x, bounds.x + m.u, bounds.x + bounds.w - q.w - m.u), y: clamp(q.pinned.y, bounds.y + m.u, bounds.y + bounds.h - q.h - m.u) };
      reqs.push(q);
    };
    for (const t of tests) {
      const box = measured.get(t.id)!;
      bx.set(t.id, box);
      // (a badge's leader leaves by its left or right side alone, as a tag's does: 2026-10-24, Ricky)
      request(t.id, t.p, box.rect.w, box.rect.h, t.label, keepOut, 2 + t.n / 10_000, 'lr');
    }
    for (const e of elevs) {
      const box = measured.get(e.id)!;
      bx.set(e.id, box);
      // (a tag's leader leaves by its left or right side alone, as Ricky draws them)
      request(e.id, e.p, box.rect.w, box.rect.h, e.label, elevKeepOut, 3 + e.n / 10_000, 'lr');
    }
    const placed = layoutLabels({ bounds, unit: m.u, radius: m.pillRadius, landing: m.labelStub, labels: reqs, obstacles, prev, lines: early, far: far > 0, farther: far > 1, keepOff: [...fixed, ...heads] });
    const taken = [...noteRects, ...rects.values()];
    let bad = 0;
    for (const p of placed) {
      if (!p.auto) continue;
      const on = (r: Rect) => overlapArea(p.rect, r) > 0;
      if (placed.some((o) => o !== p && on(o.rect)) || taken.some(on) || dotBoxes.some((d) => d !== ownDot.get(p.id) && on(d))) bad++;
    }
    return { placed, boxes: bx, bad };
  };
  // Near first; where the labels will not all fit without lying on something, with farther rings too,
  // and farther still (a heap of tests) — the best of these when none fits them all. Never smaller: a
  // badge is the same size on every map. While a label is carried nothing else may move: the labels
  // stay as they were.
  const was = prev.get('__badges');
  const tries: number[] = holding && was ? [was.y] : [0, 1, 2];
  let best: (ReturnType<typeof placeSpots> & { far: number }) | null = null;
  for (const far of tries) {
    const got = { ...placeSpots(far), far };
    if (!best || got.bad < best.bad) best = got;
    if (got.bad === 0) break;
  }
  const { placed } = best!;
  const out: Layout = { tests: new Map(), elevs: new Map(), soilsLeaders: new Map(), callouts: calloutBoxes, notes: noteBoxes, shared: new Map(), areaLabel: null, failLabel: null, badges: { far: best!.far }, dot, elevDot, legend, autoLabels: new Set([...tests, ...elevs].filter((sp) => !sp.label).map((sp) => sp.id)) };
  const elevIds = new Set(elevs.map((e) => e.id));
  for (const p of placed) {
    const box = best!.boxes.get(p.id);
    if (box) (elevIds.has(p.id) ? out.elevs : out.tests).set(p.id, { ...box, rect: p.rect });
  }
  for (const sl of shared) out.shared.set(sl.id, { ...boxes.get(sl.id)!, rect: rects.get(sl.id)! });
  out.areaLabel = out.shared.get(markupLabelId(1)) ?? null;
  out.failLabel = out.shared.get(FAIL_LABEL) ?? null;

  // Every leader now: the callouts' arrows (and the shared labels') as routed above; the tests' and
  // shots' leaders then go round everything, arrows included.
  const ghost = carriedLabel?.adrift ? carriedLabel.id : null;
  const labelRects = [...out.tests, ...out.elevs].filter(([id]) => id !== ghost).map(([, b]) => b.rect);
  for (const c of callouts) {
    const size = sizes.get(c.id)!;
    calloutBoxes.set(c.id, { ...pillLabel(size, c.look), rect: rects.get(c.id)!, leaders: routed.get(c.id)!, ...(size.editing ? { editing: true } : {}) });
  }
  for (const sl of shared) {
    const aims = sl.aims.filter((a) => !a.off);
    out.soilsLeaders.set(sl.id, routed.get(sl.id)!.map((r, k): SoilsLeader => ({ pts: r.pts, tip: r.tip, dir: r.dir, radii: r.radii ?? r.pts.map(() => 0), clear: r.clear, target: r.tip, ...(aims[k]?.mark ? { of: aims[k]!.mark!.id } : {}), face: r.face })));
  }
  // then the test labels' and shots' leaders, round every box, every other dot and the leaders before them
  const boxRects = [...noteRects, ...[...rects].filter(([id]) => id !== adrift).map(([, r]) => r), ...labelRects];
  const lines: Vec[][] = [...drawn];
  // (the leader's end wants room beyond the dot's ring, as drawn)
  const optsFor = (reach: number, sides?: 'lr'): SoilsLeaderOpts => ({ radius: m.pillRadius, landing: m.labelStub, tipInset: reach + m.gap * 0.5, ease: 1.5 * m.landing, finish: 0, ...(sides ? { sides } : {}) });
  const allDots = [...tests.map((t) => ({ id: t.id, p: t.p, r: dotReach })), ...elevs.map((e) => ({ id: e.id, p: e.p, r: elevReach }))];
  const dotAt = new Map(allDots.map((d) => [d.id, d]));
  // a leader goes round the other dots — but not round those its own dot touches: a dot in a tight
  // knot of tests could not be reached at all, and the line runs over them into its own
  const around = (own: Rect, ownDot: string): SoilsAround => {
    const me = dotAt.get(ownDot);
    return {
      boxes: boxRects.filter((r) => r !== own),
      dots: allDots.filter((d) => d.id !== ownDot && (!me || dist(d.p, me.p) > d.r + me.r + 2.5 * m.u)),
      lines,
      clear: 1.2 * m.u,
      reach: 2.5 * m.u,
    };
  };
  const alone: SoilsAround = { boxes: [], lines: [], clear: m.u, reach: 2 * m.u };
  // the shortest first: they are the ones most likely to go straight, and stand
  const labelled = [...tests.filter((t) => out.tests.has(t.id)).map((t) => ({ id: t.id, p: t.p, rect: out.tests.get(t.id)!.rect, reach: dotReach, sides: 'lr' as const, kind: 'test' })), ...elevs.filter((e) => out.elevs.has(e.id)).map((e) => ({ id: e.id, p: e.p, rect: out.elevs.get(e.id)!.rect, reach: elevReach, sides: 'lr' as const, kind: 'elev' }))];
  labelled.sort((a, b) => distTo(a.rect, a.p) - distTo(b.rect, b.p));
  for (const s of labelled) {
    if (s.id === ghost) {
      // passing over somewhere it may not rest: a rough leader, straight to its dot (drawn faded)
      out.soilsLeaders.set(s.id, [routeSoilsLeader(s.rect, s.p, optsFor(s.reach, s.sides), alone, bounds)]);
      continue;
    }
    const r = routeSoilsLeader(s.rect, s.p, optsFor(s.reach, s.sides), around(s.rect, s.id), bounds);
    out.soilsLeaders.set(s.id, [r]);
    lines.push(r.pts);
  }
  // Two labels whose leaders cross trade places when that uncrosses them — the tangle of a crowd,
  // each label beside the other's test — so long as neither lands on anything for it, and one of
  // them is new. Not while a label is carried or a shot typed: the others hold still then.
  if (!holding) {
    const taken = [...noteRects, ...[...rects.values()]];
    const legOf = (id: string) => out.soilsLeaders.get(id)![0]!.pts;
    const free = labelled.filter((sp) => !(tests.find((t) => t.id === sp.id) ?? elevs.find((e) => e.id === sp.id))?.label);
    const rectOf = (id: string) => (out.tests.get(id) ?? out.elevs.get(id))!.rect;
    const setRect = (id: string, r: Rect) => {
      const map = out.tests.has(id) ? out.tests : out.elevs;
      map.set(id, { ...map.get(id)!, rect: r });
    };
    for (let pass = 0; pass < 2; pass++) {
      let changed = false;
      for (let i = 0; i < free.length; i++) {
        for (let j = i + 1; j < free.length; j++) {
          const a = free[i]!,
            b = free[j]!;
          // (two that stood where they are stay: a tangle is undone as a label comes, never by moving labels that were settled)
          if (a.kind !== b.kind || (prev.has(a.id) && prev.has(b.id)) || !polylinesCross(legOf(a.id), legOf(b.id))) continue;
          // each where the other was (by its middle)
          const oa = rectOf(a.id),
            ob = rectOf(b.id);
          const ra = { x: ob.x + ob.w / 2 - oa.w / 2, y: ob.y + ob.h / 2 - oa.h / 2, w: oa.w, h: oa.h };
          const rb = { x: oa.x + oa.w / 2 - ob.w / 2, y: oa.y + oa.h / 2 - ob.h / 2, w: ob.w, h: ob.h };
          const others = [...out.tests, ...out.elevs].filter(([id]) => id !== a.id && id !== b.id).map(([, bx]) => bx.rect);
          const lands = (r: Rect, own: string) =>
            r.x >= bounds.x + m.u && r.y >= bounds.y + m.u && r.x + r.w <= bounds.x + bounds.w - m.u && r.y + r.h <= bounds.y + bounds.h - m.u && ![...others, ...taken].some((o) => overlapArea(r, o) > 0) && !dotBoxes.some((d) => d !== ownDot.get(own) && overlapArea(r, d) > 0);
          if (overlapArea(ra, rb) > 0 || !lands(ra, a.id) || !lands(rb, b.id)) continue;
          const rest = labelled.filter((sp) => sp.id !== a.id && sp.id !== b.id && sp.id !== ghost).map((sp) => legOf(sp.id));
          // (another's leader, routed round the boxes as they stood, must not run under either where it now is)
          if (rest.some((o) => polylineHitsRect(o, ra) || polylineHitsRect(o, rb))) continue;
          const near = { boxes: [...noteRects, ...[...rects].filter(([id]) => id !== adrift).map(([, r]) => r), ...others], dots: allDots, lines: [...drawn, ...rest], clear: 1.2 * m.u, reach: 2.5 * m.u };
          const la = routeSoilsLeader(ra, a.p, optsFor(a.reach, a.sides), { ...near, boxes: [...near.boxes, rb], dots: allDots.filter((d) => d.id !== a.id) }, bounds);
          const lb = routeSoilsLeader(rb, b.p, optsFor(b.reach, b.sides), { ...near, boxes: [...near.boxes, ra], dots: allDots.filter((d) => d.id !== b.id), lines: [...near.lines, la.pts] }, bounds);
          if (!la.clear || !lb.clear || polylinesCross(la.pts, lb.pts)) continue;
          const crossings = (pts: readonly Vec[]) => rest.filter((o) => polylinesCross(pts, o)).length;
          if (crossings(la.pts) + crossings(lb.pts) >= crossings(legOf(a.id)) + crossings(legOf(b.id)) + 1) continue;
          setRect(a.id, ra);
          setRect(b.id, rb);
          out.soilsLeaders.set(a.id, [la]);
          out.soilsLeaders.set(b.id, [lb]);
          changed = true;
        }
      }
      if (!changed) break;
    }
  }
  return out;
}

/** Whether a polyline runs through a box. */
function polylineHitsRect(pts: readonly Vec[], r: Rect): boolean {
  for (let i = 0; i + 1 < pts.length; i++) if (segmentIntersectsRect(pts[i]!, pts[i + 1]!, r)) return true;
  return false;
}
/** Whether two polylines cross. */
function polylinesCross(a: readonly Vec[], b: readonly Vec[]): boolean {
  for (let i = 0; i + 1 < a.length; i++) for (let j = 0; j + 1 < b.length; j++) if (segmentsIntersect(a[i]!, a[i + 1]!, b[j]!, b[j + 1]!)) return true;
  return false;
}

/**
 * A work area's body as the label layout keeps mildly off it: the cells of a grid over its box that
 * the area itself reaches into (a box, or a shape too small to divide, is one cell; a band is its
 * outline's).
 */
function areaCells(s: Shape, mk: MarkPoly | undefined, m: Metrics): Rect[] {
  const b = shapeBounds(s);
  if (s.k === 'rect') return [b];
  const pts = mk?.pts ?? (s.k === 'path' ? flattenPath(s.pts, s.h) : s.k === 'poly' ? s.pts : []);
  if (pts.length < 6) return [b];
  const cell = Math.max(24 * m.u, Math.max(b.w, b.h) / 24);
  const nx = Math.max(1, Math.ceil(b.w / cell)),
    ny = Math.max(1, Math.ceil(b.h / cell));
  if (nx * ny <= 1) return [b];
  const poly = { pts, bounds: b };
  const out: Rect[] = [];
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const r = { x: b.x + (i * b.w) / nx, y: b.y + (j * b.h) / ny, w: b.w / nx, h: b.h / ny };
      if (rectOverlapsPoly(r, poly)) out.push(r);
    }
  }
  return out.length ? out : [b];
}

/**
 * A closed outline's edge as boxes a label keeps firmly off: the outline walked in pieces about
 * `step` long, each piece's box grown by `pad` all round.
 */
function edgeBand(pts: readonly number[], pad: number, step: number): Rect[] {
  const n = pts.length / 2;
  if (n < 2) return [];
  const out: Rect[] = [];
  let x0 = pts[0]!,
    y0 = pts[1]!,
    x1 = x0,
    y1 = y0,
    run = 0;
  const flush = () => {
    out.push({ x: x0 - pad, y: y0 - pad, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad });
    run = 0;
  };
  for (let i = 1; i <= n; i++) {
    const ax = pts[2 * (i - 1)]!,
      ay = pts[2 * (i - 1) + 1]!;
    const bx = pts[2 * (i % n)]!,
      by = pts[2 * (i % n) + 1]!;
    const len = Math.hypot(bx - ax, by - ay);
    // a long side is cut into pieces of its own; short ones gather into one
    const k = Math.max(1, Math.floor(len / step));
    for (let j = 1; j <= k; j++) {
      const px = ax + ((bx - ax) * j) / k,
        py = ay + ((by - ay) * j) / k;
      x0 = Math.min(x0, px);
      y0 = Math.min(y0, py);
      x1 = Math.max(x1, px);
      y1 = Math.max(y1, py);
      run += len / k;
      if (run >= step) {
        flush();
        x0 = x1 = px;
        y0 = y1 = py;
      }
    }
  }
  if (run > 0) flush();
  return out;
}

/** A shared label (a markup's, '__area' …, or '__fail'): what it points at, where it was put by hand (null: automatic), its size. */
interface Shared {
  id: string;
  aims: LeaderAim[];
  pos: Vec | null;
  w: number;
  h: number;
}

/** A shared label's arrows: one to each of its areas, at the middle of the area's top or bottom edge (as a callout's). */
export function sharedAims(cc: CalloutContext, list: readonly WorkArea[], pins: Readonly<Record<string, ArrowPin>> = {}): LeaderAim[] {
  const { bounds } = cc.space;
  return list.flatMap((a): LeaderAim[] => {
    const mark = cc.space.marks.find((mk) => mk.id === a.id);
    if (!mark) return [];
    // an arrow put by hand stays where it was put (anywhere on the area's outline); one taken off is not drawn
    const pin = pins[a.id];
    if (pin?.off) return [{ mark, point: { x: clamp(mark.mid.x, bounds.x, bounds.x + bounds.w), y: clamp(mark.mid.y, bounds.y, bounds.y + bounds.h) }, off: true }];
    if (pin) return [{ mark, point: pin.target, pinned: true, ...(pin.face ? { face: pin.face } : {}) }];
    return [{ mark, point: { x: clamp(mark.mid.x, bounds.x, bounds.x + bounds.w), y: clamp(mark.mid.y, bounds.y, bounds.y + bounds.h) } }];
  });
}

/** The areas a shared label points at (a markup's work areas, or the failing ones), as marks. */
export function sharedMarks(doc: MapDoc, cc: CalloutContext, id: string): MarkPoly[] {
  return sharedAims(cc, areasOfLabel(doc.items, id)).map((a) => a.mark!);
}

/** How far a point is from a rectangle's middle (the order leaders are routed in). */
function distTo(r: Rect, p: Vec): number {
  return Math.hypot(r.x + r.w / 2 - p.x, r.y + r.h / 2 - p.y);
}

/** A line of the legend: its swatch, and the words saying what it stands for. */
export interface LegendEntry {
  swatch: 'marker' | 'fail' | 'retest' | 'area' | 'band' | 'elev';
  color: string;
  /** An area's border colour (its body is `color`). */
  stroke?: string;
  text: string;
}

/** What the legend lists: the tests (passing, failing, retests) and each markup on a soils map, the materials on a concrete one, and the elevation shots on either. */
export function legendEntries(doc: MapDoc): LegendEntry[] {
  const out: LegendEntry[] = [];
  if (doc.kind === 'soils') {
    const tests = doc.items.filter((i): i is TestPoint => i.t === 'test');
    const cones = tests.filter((t) => t.method === 'sandcone').length;
    // the badges say which test, green passing and red failing; in the date-and-initials style ("#6") the legend says what the number stands for
    const named = (doc.soils.testStyle ?? 'test') === 'id' ? ` ${doc.soils.date}_${doc.soils.initials}_#` : '';
    const how = cones === 0 ? 'nuclear gauge' : cones === tests.length ? 'sand cone' : 'nuclear gauge, or sand cone where it says so';
    if (tests.some((t) => t.status !== 'fail')) out.push({ swatch: 'marker', color: PASS, text: `Passing density test${named} (${how})` });
    if (tests.some((t) => t.status === 'fail')) out.push({ swatch: 'fail', color: FAIL, text: `Failing density test${named}${tests.every((t) => t.status === 'fail') ? ` (${how})` : ''}` });
    if (tests.some((t) => t.retestOf !== null)) out.push({ swatch: 'retest', color: INK, text: 'A retest says which test it retests' });
    // each markup: its colour and its own words
    const seen = new Set<string>();
    for (const n of markupsIn(doc.items)) {
      const list = areasOfLabel(doc.items, markupLabelId(n));
      // (on one line in the legend, however the label's own lines go)
      const text = areaLabelText(doc, list.length, n).replace(/\s*\n\s*/g, ' ');
      // (a map from before markups may hold areas of several colours in its one markup: each colour its line, as then)
      const colours = [...new Set(list.map((a) => a.color ?? 'green'))];
      for (const key of colours) {
        const c = areaColors(key);
        const line = colours.length > 1 ? `${text} (${areaColorName(key).toLowerCase()})` : text;
        if (seen.has(`${c.fill}|${line}`)) continue;
        seen.add(`${c.fill}|${line}`);
        out.push({ swatch: 'area', color: c.fill, stroke: c.stroke, text: line });
      }
    }
    if (areasOfLabel(doc.items, FAIL_LABEL).length) out.push({ swatch: 'area', color: FAIL_AREA_FILL, stroke: FAIL, text: 'Failing area' });
  } else {
    const used = new Set(doc.items.flatMap((i) => (i.t === 'hl' ? [i.material] : i.t === 'callout' && i.material ? [i.material] : [])));
    for (const mat of MATERIALS) if (used.has(mat.key)) out.push({ swatch: 'band', color: mat.color, text: `${mat.name} — ${mat.long}` });
    for (const key of used) if (!MATERIALS.some((x) => x.key === key)) out.push({ swatch: 'band', color: materialByKey(key).color, text: key });
  }
  // the elevation shots, on either kind of map: what the tag's word and number stand for
  if (doc.items.some((i) => i.t === 'elev')) {
    const meta = doc.elev;
    const how = [unitOf(meta), meta?.source ?? ''].filter(Boolean).join(', ');
    out.push({ swatch: 'elev', color: elevColor(meta?.color), text: `Elevation shot${meta?.what ? `, ${meta.what}` : ''} (${how})` });
  }
  return out;
}

/** The legend laid out: its box in the plan's lower left corner, and the sizes its lines are drawn at. */
export interface LegendLayout {
  rect: Rect;
  entries: LegendEntry[];
  /** Its words' size, its line pitch, its padding and its corner. */
  size: number;
  row: number;
  pad: number;
  radius: number;
  /** How wide the swatches' column is, and the space after it. */
  swatchW: number;
  gap: number;
  /** The note under the lines (a soils map's "Test locations approximate"), and its size. */
  note: string | null;
  noteSize: number;
  /** On a soils map each swatch is the very badge or tag in small (by entry; null: a drawn swatch). */
  swatches: (LabelBox | null)[];
}

/**
 * The legend's layout. A soils map's is in the map's smaller type, a line a box high, its swatches
 * the badges and tags themselves drawn small (the green test, the red one, a shot's tag), so the key
 * reads as the markup does; a concrete map's is as it always was.
 */
export function legendLayout(doc: MapDoc, m: Metrics, measure: Measurer): LegendLayout | null {
  const entries = legendEntries(doc);
  if (!entries.length) return null;
  const note = doc.kind === 'soils' ? 'Test locations approximate. Not to scale.' : null;
  const size = m.fontLegend;
  const margin = 4 * m.u;
  let tw = 0;
  for (const e of entries) tw = Math.max(tw, measure.width(e.text, 600, size));
  if (m.kind !== 'soils') {
    const lh = size * 1.5;
    const noteSize = size * 0.92;
    if (note) tw = Math.max(tw, measure.width(note, 500, noteSize) - m.legendSwatch - size * 0.6);
    const pad = m.legendPad;
    const w = pad * 2 + m.legendSwatch + size * 0.6 + tw;
    const h = pad * 2 + entries.length * lh + (note ? lh * 0.95 : 0);
    return { rect: { x: margin, y: doc.h - margin - h, w, h }, entries, size, row: lh, pad, radius: m.legendRadius, swatchW: m.legendSwatch, gap: size * 0.6, note, noteSize, swatches: entries.map(() => null) };
  }
  const row = m.legendRow;
  // every swatch the same height, a little under a line: a badge drawn at that height, a tag too
  const high = 0.85 * row;
  const k = high / (m.boxRatio * m.font);
  const style = doc.soils.testStyle ?? 'test';
  const swatches = entries.map((e): LabelBox | null => {
    if (e.swatch === 'marker' || e.swatch === 'fail') {
      const { pre, n } = badgeName({ n: e.swatch === 'fail' ? 2 : 1 }, style);
      const b = badgeBox(pre, n, '', m, measure, k);
      return { rect: { x: 0, y: 0, w: b.w, h: high }, lines: [pre + n], weights: [700], size: b.size, badge: { ...b.badge, d: high } };
    }
    if (e.swatch === 'elev') {
      const font = high / m.boxRatio;
      const n = '#1';
      const words = mapPrefix(doc.elev);
      const padX = m.padRatio * font;
      const wordsX = padX + measure.width(n, 700, font) + (words ? 0.35 * font : 0);
      const w = wordsX + (words ? measure.width(words, 700, font) : 0) + padX;
      const wordsText = layoutRich(runsOf(words), { weight: 700, size: font, color: PAPER }, null, measure);
      return { rect: { x: 0, y: 0, w, h: high }, lines: [`${n} ${words}`], weights: [700], size: font, tag: { n, words, wordsText, wordsX, wordsTop: 0, valueX: w - padX, padX, line: high, base: high / 2 + 0.36 * font } };
    }
    return null;
  });
  const areaW = 1.25 * high;
  const swatchW = Math.max(areaW, ...swatches.map((b) => b?.rect.w ?? 0));
  const gap = 0.6 * size;
  const pad = m.legendPad;
  const noteSize = size;
  const noteW = note ? measure.width(note, 500, noteSize) : 0;
  const w = Math.max(pad * 2 + swatchW + gap + tw, pad * 2 + noteW);
  const h = pad * 2 + entries.length * row + (note ? row : 0);
  return { rect: { x: margin, y: doc.h - margin - h, w, h }, entries, size, row, pad, radius: m.legendRadius, swatchW, gap, note, noteSize, swatches };
}

/** Previous rectangles for hysteresis (from a prior layout). */
export function layoutRects(l: Layout | null): Map<string, Rect> {
  const m = new Map<string, Rect>();
  if (!l) return m;
  // (a label put by hand is not kept: let go to be placed automatically, it is placed afresh)
  for (const [id, b] of l.tests) if (l.autoLabels.has(id)) m.set(id, b.rect);
  for (const [id, b] of l.elevs) if (l.autoLabels.has(id)) m.set(id, b.rect);
  for (const [id, b] of l.callouts) m.set(`${id}#${b.leaders.length}`, b.rect);
  for (const [id, b] of l.shared) m.set(`${id}#${l.soilsLeaders.get(id)?.length ?? 0}`, b.rect);
  // how far out the badges were laid out (y), so they hold still while a label is carried
  m.set('__badges', { x: 1, y: l.badges.far, w: 0, h: 0 });
  return m;
}
